/**
 * Pure logic behind the `/inbox` funnel-approval block (2026-09-24).
 *
 * Extracted from the component per the house rule that anything worth being
 * right is worth being testable without a DOM. Everything here is a function of
 * (row, now, draft) — no fetching, no state, no side effects.
 *
 * The reason this file exists rather than a few inline ternaries: the approval
 * block's job is to stop an operator doing something irreversible at the wrong
 * moment, and "irreversible at the wrong moment" is a time calculation. Meta's
 * private-reply window is 7 days from the COMMENT, so a staged DM can go stale
 * while it sits in the queue, and the page must say so BEFORE the tap rather
 * than surfacing a vendor error afterwards.
 */

import type { InstagramFunnelStagedSend } from "./api";

export const HOUR_MS = 3_600_000;

/**
 * How close this staged send is to Meta's cliff.
 *
 * `urgent` is < 24h. The threshold is not arbitrary: comment volume is roughly
 * 13 a month, so the queue is checked in days, not minutes — a warning that
 * only appeared in the last hour would routinely appear after the send was
 * already impossible.
 */
export interface FunnelExpiryState {
  readonly expired: boolean;
  readonly urgent: boolean;
  readonly hoursLeft: number;
  /** Operator-facing, already phrased. `null` when there is nothing to say. */
  readonly label: string | null;
}

export function funnelExpiryState(
  expiresAtIso: string,
  nowMs: number,
): FunnelExpiryState {
  const at = Date.parse(expiresAtIso);
  if (Number.isNaN(at)) {
    // An unparseable stamp must NOT read as "plenty of time". The row came from
    // our own NOT NULL timestamptz column, so this is a bug rather than a
    // state — and the safe reading of a bug here is "do not send".
    return {
      expired: true,
      urgent: true,
      hoursLeft: 0,
      label: "expiry unreadable",
    };
  }
  const hoursLeft = (at - nowMs) / HOUR_MS;
  if (hoursLeft <= 0) {
    return { expired: true, urgent: true, hoursLeft: 0, label: "window closed" };
  }
  if (hoursLeft < 24) {
    return {
      expired: false,
      urgent: true,
      hoursLeft,
      label: `${Math.max(1, Math.round(hoursLeft))}h left`,
    };
  }
  return {
    expired: false,
    urgent: false,
    hoursLeft,
    label: `${Math.round(hoursLeft / 24)}d left`,
  };
}

/** Meta's per-message cap for a private reply, matched to the action's schema. */
export const FUNNEL_BODY_MAX = 900;

export interface FunnelSendability {
  readonly canSend: boolean;
  /** Why not. `null` when it can send. */
  readonly blockedReason: string | null;
  /** true when the operator changed the body — drives the "Edit & send" copy. */
  readonly edited: boolean;
}

/**
 * Can this staged send go out right now, with this draft?
 *
 * Every `false` here is also enforced server-side — the action re-checks the
 * status, the window and the body length at the chokepoint. This is the
 * courtesy layer, not the guard: it exists so the operator learns "too late"
 * from the page instead of from a vendor error after an irreversible attempt.
 */
export function funnelSendability(
  row: InstagramFunnelStagedSend,
  draft: string,
  nowMs: number,
): FunnelSendability {
  const trimmed = draft.trim();
  const edited = trimmed !== row.staged_body.trim();
  if (row.status !== "staged") {
    return {
      canSend: false,
      blockedReason: `already ${row.status}`,
      edited,
    };
  }
  const expiry = funnelExpiryState(row.private_reply_expires_at, nowMs);
  if (expiry.expired) {
    return {
      canSend: false,
      // Said plainly, because the instinct on seeing a dead button is to retry.
      blockedReason:
        "the 7-day private-reply window closed — Meta will not accept it, and no retry reopens it",
      edited,
    };
  }
  if (trimmed.length === 0) {
    return { canSend: false, blockedReason: "the message is empty", edited };
  }
  if (trimmed.length > FUNNEL_BODY_MAX) {
    return {
      canSend: false,
      blockedReason: `${trimmed.length} characters — this edge cannot split a message, so ${FUNNEL_BODY_MAX} is the hard cap`,
      edited,
    };
  }
  return { canSend: true, blockedReason: null, edited };
}

/**
 * The one-line status an operator reads at a glance.
 *
 * `auto` is called out even when staged: an auto-mode campaign sends without a
 * tap, so a human seeing it here should know the button is not what will send
 * it — otherwise a row that vanishes on its own looks like a bug.
 */
export function describeFunnelSend(
  row: InstagramFunnelStagedSend,
  nowMs: number,
): string {
  switch (row.status) {
    case "sent":
      return "sent";
    case "skipped":
      return "skipped";
    case "expired":
      return "expired — the 7-day window closed before it was sent";
    case "failed":
      return row.error_code !== null ? `failed (${row.error_code})` : "failed";
    case "staged": {
      const e = funnelExpiryState(row.private_reply_expires_at, nowMs);
      const when = e.label !== null ? `, ${e.label}` : "";
      return row.mode === "auto"
        ? `queued — this campaign sends automatically${when}`
        : `awaiting approval${when}`;
    }
  }
}

/**
 * Does approving this row ALSO post a public comment reply?
 *
 * Only in `approve` mode, and only when the campaign has copy for it. The
 * BEHAVIOUR is unchanged since 2026-09-24; the REASON is not, and the old reason
 * is now false, so it is rewritten rather than left to rot (invariant 1).
 *
 * It used to be "in `auto` mode the funnel deliberately posts nothing publicly,
 * because `reply_instagram_comment` admits `human:staff` and `system:cli` only".
 * Since 2026-09-26 that action DOES admit `system:saga` behind scope
 * `instagram:reply_comment`, and an `auto` campaign with `auto_public_reply`
 * armed posts its own reply.
 *
 * So the answer here is still `null` for `auto`, for a simpler reason: THERE IS
 * NO APPROVAL. An auto row is sent by the router's `auto_send` step, and its
 * public reply — if armed — is posted by the same step, at send time. This
 * function answers "what does the approver's tap cause", and for an auto row the
 * approver's tap causes nothing because there is nothing left to tap.
 *
 * In `approve` mode the reply rides the approver's OWN staff actor, as before.
 */
export function publicReplyOnApprove(
  row: InstagramFunnelStagedSend,
): string | null {
  if (row.mode !== "approve") return null;
  const body = row.public_reply_body;
  if (body === null) return null;
  const trimmed = body.trim();
  return trimmed.length > 0 ? trimmed : null;
}
